import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { vestResolver } from "@hookform/resolvers/vest";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import Dialog from "components/dialog";
import ErrorSnackbarContent from "components/error-snackbar-content";
import { displayNumber } from "features/calls/dialstack/call-display.util";
import { numberBelongsToContact } from "features/calls/dialstack/unknown-caller/save-contact-dialog.component";
import {
  CallContact,
  DialstackContact,
  useCreateContactMutation,
  useGetContactQuery,
  useUpdateContactMutation
} from "services/dialstack";

import ContactFields from "./contact-fields.component";
import {
  ContactFormValues,
  EMPTY_CONTACT_FORM,
  contactToFormValues,
  contactValidator,
  formValuesToInput,
  phoneDigits
} from "./contact-form.validator";

type ContactFormDialogProps = {
  // Set to edit; absent to create.
  contact?: DialstackContact;
  // Create only: prefill (e.g. a number from the dialer).
  initialValues?: Partial<ContactFormValues>;
  onClose: () => void;
  onSaved: (contact: DialstackContact) => void;
  onOpenContact: (contact: CallContact) => void;
  // Edit only: fold this contact into the number's owner instead.
  onMergeInto?: (owner: CallContact) => void;
};

type Conflict = { owner: CallContact };

// The shared Dialog ignores MUI's `maxWidth` (its Paper is `maxWidth: 100%`)
// and, under its 1321 px "mobile" breakpoint, pins the Paper's minWidth to
// the viewport — so the width is set on the Paper itself: two form columns
// at 720 px, never wider than the viewport less a margin.
export const CONTACT_DIALOG_PAPER_SX = {
  width: 720,
  minWidth: 560,
  maxWidth: "calc(100% - 40px)"
} as const;
export const CONTACT_DIALOG_SUBTITLE_SX = {
  color: "text.primary",
  fontSize: 14
} as const;

// Which of the form's numbers the owner already has — the 409 names the
// owner, not the number, so the owner's own list settles it.
function conflictingRows(
  values: ContactFormValues,
  owner: DialstackContact | undefined
) {
  if (!owner) return [];
  const owned = new Set(owner.phone_numbers.map(({ number }) => number));
  return values.phone_numbers
    .map((row, index) => ({ index, number: phoneDigits(row.number) }))
    .filter((row) => owned.has(row.number));
}

// Create or edit a Rolodex contact (ENG-2177). A number another contact
// already owns is never silently re-pointed: the save is refused and the
// owner is offered instead.
export default function ContactFormDialog({
  contact,
  initialValues,
  onClose,
  onSaved,
  onOpenContact,
  onMergeInto
}: ContactFormDialogProps) {
  const dispatch = useAppDispatch();
  const isEdit = Boolean(contact);
  const form = useForm<ContactFormValues>({
    mode: "onBlur",
    resolver: vestResolver(contactValidator()),
    defaultValues: contact
      ? contactToFormValues(contact)
      : { ...EMPTY_CONTACT_FORM, ...initialValues }
  });
  const { isDirty } = form.formState;
  const [createContact, { isLoading: isCreating }] = useCreateContactMutation();
  const [updateContact, { isLoading: isUpdating }] = useUpdateContactMutation();
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const { data: owner } = useGetContactQuery(conflict?.owner.id ?? 0, {
    skip: !conflict
  });
  const ownerRow = conflict ? owner : undefined;
  const isSubmitting = isCreating || isUpdating;
  const alertRef = useRef<HTMLDivElement | null>(null);

  const submit = form.handleSubmit(async (values) => {
    setConflict(null);
    const input = formValuesToInput(values);
    const response = contact
      ? await updateContact({ id: contact.id, ...input })
      : await createContact(input);
    if ("error" in response) {
      const conflictOwner = numberBelongsToContact(response.error);
      if (conflictOwner) {
        setConflict({ owner: conflictOwner });
        return;
      }
      dispatch(setCustomMessage(<ErrorSnackbarContent response={response} />));
      dispatch(setOpen(true));
      return;
    }
    dispatch(setCustomMessage(isEdit ? "Contact updated" : "Contact added"));
    dispatch(setOpen(true));
    onSaved(response.data);
  });

  const conflicts = conflictingRows(form.getValues(), ownerRow);
  const conflictNumbers = conflicts.map(({ number }) => displayNumber(number));

  // The refusal is brought into view and read out, and the number fields
  // it is about are marked, so the person sees which row to change.
  useEffect(() => {
    if (!conflict) return;
    alertRef.current?.scrollIntoView({ block: "nearest" });
    alertRef.current?.focus();
  }, [conflict]);
  const conflictIndexes = conflicts.map(({ index }) => index).join(",");
  useEffect(() => {
    if (!conflict || !conflictIndexes) return;
    for (const index of conflictIndexes.split(",")) {
      form.setError(`phone_numbers.${Number(index)}.number`, {
        type: "conflict",
        message: `Already used by ${conflict.owner.name}`
      });
    }
  }, [conflict, conflictIndexes, form]);

  const removeConflictingNumbers = () => {
    const taken = new Set(conflicts.map(({ number }) => number));
    const remaining = form
      .getValues("phone_numbers")
      .filter((row) => !taken.has(phoneDigits(row.number)));
    form.clearErrors("phone_numbers");
    form.setValue(
      "phone_numbers",
      remaining.length > 0 ? remaining : [{ number: "", label: "" }],
      { shouldDirty: true }
    );
    setConflict(null);
  };

  return (
    <Dialog
      title={isEdit ? "Edit contact" : "Add contact"}
      subtitle={
        isEdit
          ? undefined
          : "Calls from these numbers will show the contact's name."
      }
      subtitleTextSx={CONTACT_DIALOG_SUBTITLE_SX}
      open
      onClose={onClose}
      minWidth={560}
      // "md" lifts MUI's default 600 px "sm" cap; the Paper sx sets the width.
      maxWidth="md"
      paperContentSx={CONTACT_DIALOG_PAPER_SX}
      actions={
        <>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={isSubmitting}
            onClick={submit}
            data-testid="contact-form-submit"
          >
            {isEdit ? "Save changes" : "Save contact"}
          </LoadingButton>
        </>
      }
    >
      <FormProvider {...form}>
        <Stack
          component="form"
          spacing={2}
          sx={{ pt: 1, pb: 1 }}
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
          data-testid="contact-form"
        >
          <ContactFields autoFocusName={!isEdit} />
          {conflict && (
            <Alert
              ref={alertRef}
              severity="warning"
              role="alert"
              tabIndex={-1}
              data-testid="contact-form-conflict"
            >
              <Typography variant="body2">
                {conflictNumbers.length > 0
                  ? conflictNumbers.join(", ")
                  : "A number here"}{" "}
                already belongs to <strong>{conflict.owner.name}</strong>
                {conflict.owner.organization
                  ? ` (${conflict.owner.organization})`
                  : ""}
                . Nothing was saved.
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1.5 }}
              >
                {isEdit && onMergeInto && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={() => onMergeInto(conflict.owner)}
                    data-testid="contact-form-conflict-merge"
                  >
                    Merge into {conflict.owner.name}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => onOpenContact(conflict.owner)}
                  data-testid="contact-form-conflict-open"
                >
                  Open {conflict.owner.name}
                  {isDirty ? " (discards this form)" : ""}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={removeConflictingNumbers}
                  disabled={conflicts.length === 0}
                  data-testid="contact-form-conflict-remove"
                >
                  {conflictNumbers.length > 0
                    ? `Remove ${conflictNumbers.join(" and ")} from this form`
                    : "Remove number"}
                </Button>
              </Stack>
            </Alert>
          )}
        </Stack>
      </FormProvider>
    </Dialog>
  );
}
